import { useState, useMemo, useRef, useEffect, useCallback } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { toast } from 'react-hot-toast'
import { GitBranch, Plus, Search, AlertCircle, Calendar } from 'lucide-react'
import { useSpace, useIssues, useStatuses, useIssueMutations } from '../hooks'
import { cn } from '../utils'
import { IssueModal } from '../components/IssueModal'
import { NewIssueModal } from '../components/NewIssueModal'
import { IssueActionsMenu } from '../components/IssueActionsMenu'
import { getIssueErrorMessage } from '../api'
import { useUserOptions } from '../../../shared/hooks/useUserOptions'
import {
  type ZoomLevel,
  ZOOM_COL_WIDTH,
  buildDayCols,
  buildWeekCols,
  buildMonthCols,
  buildQuarterCols,
  computeTimelineRange,
  startOfDayLocal,
  dateToPixel,
  calculateTaskBar,
} from '../timelineUtils'

export function TimelinePage() {
  const params = useParams<{ id?: string; spaceId?: string }>()
  const spaceId = params.id || params.spaceId || ''
  const [searchParams, setSearchParams] = useSearchParams()
  const selectedIssueId = searchParams.get('selectedIssue')

  const { data: space } = useSpace(spaceId ?? null)
  const { data: issues = [], isLoading } = useIssues(spaceId ?? null)
  const { data: statuses = [] } = useStatuses(spaceId ?? null)
  const { data: userOptions = [] } = useUserOptions(spaceId ?? null)
  const { create, update, remove } = useIssueMutations(spaceId ?? null)

  const [zoom, setZoom] = useState<ZoomLevel>('months')
  const [search, setSearch] = useState('')
  const [modalOpen, setModalOpen] = useState(false)



  const selectedIssue = useMemo(() => {
    if (!selectedIssueId) return null
    return (issues as any[]).find((i) => i.id === selectedIssueId || i.key === selectedIssueId) ?? null
  }, [issues, selectedIssueId])

  const handleOpenIssue = useCallback((issueId: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      next.set('selectedIssue', issueId)
      return next
    })
  }, [setSearchParams])

  const handleCloseIssueModal = useCallback(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      next.delete('selectedIssue')
      return next
    })
  }, [setSearchParams])

  const handleDelete = async (id: string) => {
    try {
      await remove.mutateAsync(id)
      if (selectedIssueId === id) handleCloseIssueModal()
    } catch {
      toast.error(getIssueErrorMessage(new Error('delete'), 'delete this issue'))
    }
  }

  const handleMoveOnList = async (issueId: string, targetStatusId: string) => {
    try {
      await update.mutateAsync({ id: issueId, payload: { status: targetStatusId } })
    } catch {
      toast.error(getIssueErrorMessage(new Error('move'), 'move this issue'))
    }
  }

  const filteredIssues = useMemo(() => {
    const q = search.trim().toLowerCase()
    return (issues as any[]).filter(
      (i) =>
        !q ||
        (i.title || '').toLowerCase().includes(q) ||
        (i.key || '').toLowerCase().includes(q),
    )
  }, [issues, search])

  const allSpaceIssues = useMemo(() => (issues as any[]), [issues])

  // Split into scheduled + unscheduled
  const { scheduledIssues, unscheduledIssues } = useMemo(() => {
    const s: any[] = []
    const u: any[] = []
    for (const issue of filteredIssues) {
      if (issue.startDate || issue.start_date || issue.due_date) {
        s.push(issue)
      } else {
        u.push(issue)
      }
    }
    return { scheduledIssues: s, unscheduledIssues: u }
  }, [filteredIssues])

  // Timeline columns — real calendar periods derived from task dates
  const timelineCols = useMemo(() => {
    const { from, to } = computeTimelineRange(zoom, allSpaceIssues, space)
    switch (zoom) {
      case 'days':
        return buildDayCols(from, to)
      case 'weeks':
        return buildWeekCols(from, to)
      case 'months':
        return buildMonthCols(from, to)
      case 'quarters':
        return buildQuarterCols(from, to)
      default:
        return []
    }
  }, [zoom, allSpaceIssues, space])

  const colWidth = ZOOM_COL_WIDTH[zoom]
  const totalGridWidth = Math.max(timelineCols.length * colWidth, 700)

  // ── Bar style calculation from task schedule ─────────────────────────────

  const getBarStyle = useCallback(
    (issue: any): React.CSSProperties => {
      const pos = calculateTaskBar(issue, timelineCols, colWidth)
      if (!pos.visible) {
        return { display: 'none' }
      }
      return {
        left: `${pos.leftPx}px`,
        width: `${pos.widthPx}px`,
      }
    },
    [timelineCols, colWidth],
  )

  const getRowHighlight = useCallback(
    (issue: any): 'today' | 'past' | 'future' | 'none' => {
      const due = issue.due_date ? new Date(issue.due_date) : null
      if (!due) return 'none'
      const todayStart = startOfDayLocal(new Date())
      const dueStart = startOfDayLocal(due)
      if (dueStart.getTime() === todayStart.getTime()) return 'today'
      if (dueStart < todayStart) return 'past'
      return 'future'
    },
    [],
  )

  // ── Status color helper ──────────────────────────────────────────────────

  const barBg = (issue: any) => {
    if (issue.status_category === 'done') return 'bg-emerald-500'
    if (issue.priority === 'critical' || issue.priority === 'high') return 'bg-red-500'
    return 'bg-blue-600'
  }

  // ── Row rendering ────────────────────────────────────────────────────────

  const renderRow = (issue: any) => {
    const style = getBarStyle(issue)
    if ((style as any).display === 'none') return null
    const highlight = getRowHighlight(issue)

    return (
      <div
        key={issue.id}
        className={cn(
          'relative h-11 flex items-center border-b border-gray-100 dark:border-slate-800/60',
          highlight === 'today' && 'bg-amber-50/60 dark:bg-amber-950/20',
          highlight === 'past' && 'bg-red-50/40 dark:bg-red-950/10',
        )}
        style={{ width: `${totalGridWidth}px` }}
      >
        <div
          onClick={() => handleOpenIssue(issue.id)}
          className={cn(
            'absolute h-6 rounded-md px-2.5 flex items-center text-white text-[11px] font-medium shadow-xs select-none transition-all cursor-pointer hover:brightness-105 hover:shadow-md overflow-hidden',
            barBg(issue),
          )}
          style={style}
          title={`${issue.key}: ${issue.title}`}
        >
          <span className="truncate w-full text-center pointer-events-none">{issue.title}</span>
        </div>
      </div>
    )
  }

  // ── "Today" marker ───────────────────────────────────────────────────────

  const todayX = dateToPixel(new Date(), timelineCols, colWidth)

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-t-transparent" />
      </div>
    )
  }

  return (
    <div className="flex h-full flex-col bg-white dark:bg-slate-950 font-sans">
      {/* Top Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-4 border-b border-gray-200 px-6 py-3 dark:border-slate-800">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <GitBranch className="h-5 w-5 text-blue-600" />
            <h2 className="text-base font-bold text-gray-900 dark:text-slate-100">Timeline / Roadmap</h2>
          </div>
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-gray-400" />
            <input
              type="text"
              placeholder="Filter timeline..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-8 rounded-lg border border-gray-200 pl-8 pr-3 text-xs dark:border-slate-800 dark:bg-slate-900 dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-blue-500 w-48"
            />
          </div>
        </div>

        <div className="flex items-center gap-3">
          {/* Zoom Selector: Days | Weeks | Months | Quarters */}
          <div className="flex rounded-lg border border-gray-200 bg-gray-50 p-0.5 dark:border-slate-800 dark:bg-slate-900">
            {(['days', 'weeks', 'months', 'quarters'] as ZoomLevel[]).map((z) => (
              <button
                key={z}
                onClick={() => setZoom(z)}
                className={cn(
                  'rounded-md px-2.5 py-1 text-xs font-medium capitalize transition-colors',
                  zoom === z
                    ? 'bg-white text-gray-900 shadow-xs dark:bg-slate-800 dark:text-slate-100 font-semibold'
                    : 'text-gray-500 hover:text-gray-900 dark:text-slate-400 dark:hover:text-slate-200',
                )}
              >
                {z}
              </button>
            ))}
          </div>

          <button
            onClick={() => setModalOpen(true)}
            className="flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white shadow-xs hover:bg-blue-700"
          >
            <Plus className="h-3.5 w-3.5" /> Add Item
          </button>
        </div>
      </div>

      {/* Main Gantt / Timeline View */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left Tasks Tree Column */}
        <div className="w-80 border-r border-gray-200 dark:border-slate-800 flex flex-col shrink-0 bg-white dark:bg-slate-950">
          <div className="border-b border-gray-200 bg-gray-50/80 px-4 py-2.5 text-xs font-bold text-gray-700 dark:border-slate-800 dark:bg-slate-900/50 dark:text-slate-300">
            Work Items ({scheduledIssues.length + unscheduledIssues.length})
          </div>
          <div className="flex-1 overflow-y-auto divide-y divide-gray-100 dark:divide-slate-800/60 scrollbar-thin">
            {/* Scheduled tasks — have at least one date */}
            {scheduledIssues.map((issue: any) => (
              <div
                key={issue.id}
                onClick={() => handleOpenIssue(issue.id)}
                className="flex items-center justify-between px-4 py-2.5 hover:bg-blue-50/40 dark:hover:bg-slate-900/40 text-xs transition-colors h-11 cursor-pointer group"
              >
                <div className="flex items-center gap-2 min-w-0 pr-2">
                  <span className="font-mono text-[10px] text-gray-500 dark:text-slate-400 font-semibold shrink-0">
                    {issue.key}
                  </span>
                  <span className="truncate font-medium text-gray-800 dark:text-slate-200 group-hover:text-blue-600 transition-colors">
                    {issue.title}
                  </span>
                </div>
                <IssueActionsMenu
                  onEdit={() => handleOpenIssue(issue.id)}
                  onDelete={() => handleDelete(issue.id)}
                  
                  
                />
              </div>
            ))}
            {/* Unscheduled tasks — no dates */}
            {unscheduledIssues.length > 0 && (
              <>
                <div className="px-4 py-1.5 text-[10px] font-bold uppercase tracking-wider text-gray-400 dark:text-slate-500 bg-gray-50/50 dark:bg-slate-900/30">
                  Unscheduled ({unscheduledIssues.length})
                </div>
                {unscheduledIssues.map((issue: any) => (
                  <div
                    key={issue.id}
                    onClick={() => handleOpenIssue(issue.id)}
                    className="flex items-center justify-between px-4 py-2.5 text-xs transition-colors h-11 cursor-pointer group opacity-70"
                  >
                    <div className="flex items-center gap-2 min-w-0 pr-2">
                      <span className="font-mono text-[10px] text-gray-400 dark:text-slate-500 font-semibold shrink-0">
                        {issue.key}
                      </span>
                      <span className="truncate font-medium text-gray-600 dark:text-slate-400 group-hover:text-blue-600 transition-colors">
                        {issue.title}
                      </span>
                    </div>
                    <IssueActionsMenu
                      onEdit={() => handleOpenIssue(issue.id)}
                      onDelete={() => handleDelete(issue.id)}
                      
                      
                    />
                  </div>
                ))}
              </>
            )}
            {scheduledIssues.length === 0 && unscheduledIssues.length === 0 && (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <Calendar className="h-8 w-8 text-gray-300 dark:text-slate-600 mb-2" />
                <p className="text-xs text-gray-400 dark:text-slate-500">No work items found</p>
              </div>
            )}
          </div>
        </div>

        {/* Right Gantt Grid */}
        <div className="flex-1 overflow-x-auto overflow-y-auto bg-gray-50/30 dark:bg-slate-900/10 scrollbar-thin">
          <div style={{ width: `${totalGridWidth}px`, minWidth: `${totalGridWidth}px` }}>
            {/* Header row with period labels */}
            <div
              className="flex border-b border-gray-200 bg-gray-50/80 dark:border-slate-800 dark:bg-slate-900/50 sticky top-0 z-10"
              style={{ width: `${totalGridWidth}px` }}
            >
              {timelineCols.map((col) => (
                <div
                  key={col.id}
                  title={col.label}
                  className={cn(
                    'border-r border-gray-200 text-center font-semibold text-gray-500 dark:border-slate-800 dark:text-slate-400 whitespace-nowrap shrink-0 overflow-hidden box-border',
                    zoom === 'days' ? 'px-0.5 py-1 text-[10px]' : 'px-3 py-2 text-[11px]',
                  )}
                  style={{
                    width: `${colWidth}px`,
                    minWidth: `${colWidth}px`,
                    maxWidth: `${colWidth}px`,
                    boxSizing: 'border-box',
                  }}
                >
                  {zoom === 'days' ? (
                    <div className="flex flex-col items-center justify-center leading-tight">
                      <span className="text-[9px] uppercase tracking-wider text-gray-400 dark:text-slate-500 font-semibold leading-none">
                        {col.startDate.toLocaleDateString('en-US', { weekday: 'narrow' })}
                      </span>
                      <span
                        className={cn(
                          'text-[11px] font-bold leading-none mt-1',
                          col.startDate.getDay() === 0 || col.startDate.getDay() === 6
                            ? 'text-gray-400 dark:text-slate-500'
                            : 'text-gray-700 dark:text-slate-200',
                        )}
                      >
                        {col.startDate.getDate()}
                      </span>
                    </div>
                  ) : (
                    col.label
                  )}
                </div>
              ))}
            </div>

            {/* Task rows with date-driven bars */}
            <div className="relative" style={{ width: `${totalGridWidth}px` }}>
              {/* Background column guides */}
              <div className="absolute inset-0 flex pointer-events-none">
                {timelineCols.map((col) => (
                  <div
                    key={`guide-${col.id}`}
                    className={cn(
                      'border-r border-gray-100 dark:border-slate-800/40 shrink-0 h-full box-border',
                      zoom === 'days' &&
                        (col.startDate.getDay() === 0 || col.startDate.getDay() === 6) &&
                        'bg-gray-100/35 dark:bg-slate-900/35',
                    )}
                    style={{
                      width: `${colWidth}px`,
                      minWidth: `${colWidth}px`,
                      maxWidth: `${colWidth}px`,
                      boxSizing: 'border-box',
                    }}
                  />
                ))}
              </div>

              {/* Today marker */}
              {todayX >= 0 && todayX <= totalGridWidth && (
                <div
                  className="absolute top-0 bottom-0 z-20 pointer-events-none"
                  style={{ left: `${todayX}px` }}
                >
                  <div className="w-px h-full bg-red-400/70 dark:bg-red-500/60" />
                  <span className="absolute -top-0.5 left-1 text-[9px] font-bold text-red-500 dark:text-red-400 whitespace-nowrap">
                    Today
                  </span>
                </div>
              )}

              {/* Task rows */}
              <div className="relative z-10">
                {scheduledIssues.map((issue: any) => renderRow(issue))}
              </div>

              {/* Empty state */}
              {scheduledIssues.length === 0 && (
                <div className="flex flex-col items-center justify-center py-20 text-center">
                  <Calendar className="h-10 w-10 text-gray-300 dark:text-slate-600 mb-3" />
                  <p className="text-sm text-gray-400 dark:text-slate-500 font-medium">
                    No scheduled tasks
                  </p>
                  <p className="text-xs text-gray-400 dark:text-slate-500 mt-1">
                    Add tasks with start/due dates to see them here
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* New Issue Modal (opens on "Add Item" click) */}
      {spaceId && (
        <NewIssueModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          spaceId={spaceId}
        />
      )}

      {/* Task Details Modal (opens when clicking task row or bar) */}
      {selectedIssue && (
        <IssueModal
          issue={selectedIssue}
          open={!!selectedIssue}
          onClose={handleCloseIssueModal}
          statuses={statuses}
          members={userOptions}
          epics={(issues as any[]).filter((i) => i.type === 'epic')}
          departments={(space as any)?.departments}
          onUpdate={async (id, payload) => {
            await update.mutateAsync({ id, payload })
          }}
          onDelete={async (id) => {
            await remove.mutateAsync(id)
            handleCloseIssueModal()
          }}
        />
      )}
    </div>
  )
}
